import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Check, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';

const plans = [
  {
    name: 'Starter',
    price: '$2,999',
    description: 'Perfect for small businesses and startups',
    features: [
      '5-page responsive website',
      'Mobile-friendly design',
      'Basic SEO optimization',
      'Contact form integration',
      '1 month support',
      'Fast delivery (2 weeks)',
    ],
    popular: false,
  },
  {
    name: 'Professional',
    price: '$7,999',
    description: 'Ideal for growing businesses',
    features: [
      'Up to 15 pages',
      'Custom design & animations',
      'Advanced SEO optimization',
      'CMS integration',
      'E-commerce (up to 50 products)',
      '3 months support',
      'Priority delivery',
    ],
    popular: true,
  },
  {
    name: 'Enterprise',
    price: 'Custom',
    description: 'For large-scale projects',
    features: [
      'Unlimited pages',
      'Custom software development',
      'Mobile app development',
      'API integrations',
      'Advanced features',
      '12 months support',
      'Dedicated team',
      'Priority support',
    ],
    popular: false,
  },
];

export default function PricingPage() {
  return (
    <>
      <Helmet>
        <title>Pricing - FuturePath Technologies</title>
        <meta name="description" content="Transparent pricing for web development, mobile apps, and software solutions. Find the perfect plan for your business." />
      </Helmet>

      <section className="relative min-h-[40vh] flex items-center justify-center pt-32 pb-20">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-secondary/10 to-transparent opacity-50" />
        <div className="container-custom relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl md:text-6xl font-bold font-outfit mb-6 text-white" data-testid="pricing-hero-title">
              Transparent Pricing
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
              Choose the perfect plan for your business needs
            </p>
          </motion.div>
        </div>
      </section>

      <section className="section-padding" data-testid="pricing-plans-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {plans.map((plan, index) => (
              <motion.div
                key={plan.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                className="relative"
                data-testid={`pricing-plan-${index}`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-gradient-to-r from-primary to-accent rounded-full text-sm font-semibold text-white">
                    Most Popular
                  </div>
                )}
                <Card
                  className={`glass-card p-8 rounded-3xl h-full flex flex-col ${
                    plan.popular ? 'border-2 border-primary neon-glow' : ''
                  }`}
                >
                  <div>
                    <h3 className="text-2xl font-bold font-outfit text-white mb-2">
                      {plan.name}
                    </h3>
                    <div className="flex items-baseline gap-2 mb-4">
                      <span className="text-5xl font-bold font-outfit text-primary">
                        {plan.price}
                      </span>
                      {plan.price !== 'Custom' && <span className="text-muted-foreground">USD</span>}
                    </div>
                    <p className="text-muted-foreground mb-6">{plan.description}</p>
                  </div>

                  <ul className="space-y-3 mb-8 flex-grow">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <Check size={20} className="text-primary mt-0.5 flex-shrink-0" />
                        <span className="text-muted-foreground text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Button
                    asChild
                    size="lg"
                    className={`w-full rounded-full ${
                      plan.popular
                        ? 'bg-primary hover:bg-primary/90 neon-glow'
                        : 'bg-white/10 hover:bg-white/20'
                    }`}
                    data-testid={`pricing-cta-${index}`}
                  >
                    <Link to="/consultation">
                      Get Started
                      <ArrowRight className="ml-2" size={20} />
                    </Link>
                  </Button>
                </Card>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-16">
            <p className="text-muted-foreground text-lg mb-4">
              Need a custom solution? Let's discuss your specific requirements.
            </p>
            <Button asChild size="lg" variant="outline" className="rounded-full" data-testid="custom-pricing-btn">
              <Link to="/contact">Contact for Custom Pricing</Link>
            </Button>
          </div>
        </div>
      </section>
    </>
  );
}
